import { Link, Stack, useRouter } from "expo-router";
import { useMemo, useState } from "react";
import {
  KeyboardAvoidingView,
  Platform,
  StyleSheet,
  Text,
  TextInput,
  View,
} from "react-native";

import { fontFamily, layout, radius, space, textStyle } from "@passfirst/ui";

import { Button } from "../src/components/button";
import { API_URL, APP_URL } from "../src/env";
import { log } from "../src/log";
import { type Colors, useColors } from "../src/theme";

export default function ForgotPassword(): JSX.Element {
  const router = useRouter();
  const colors = useColors();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  async function handleSubmit(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      // BetterAuth's reset-password request endpoint. The client SDK
      // surfaces resetPassword (step 2) but not the request step, so
      // we hit the HTTP endpoint directly. redirectTo is what the
      // /auth/reset-password/:token callback redirects to after
      // validating the token — landing the user on /reset-password
      // with ?token=<value> in the URL.
      const res = await fetch(`${API_URL}/auth/request-password-reset`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          email,
          redirectTo: `${APP_URL}/reset-password`,
        }),
      });
      if (!res.ok) {
        const body = (await res.json().catch((parseErr) => {
          log.warn({ status: res.status, err: String(parseErr) }, "forgot-password response not JSON");
          return {};
        })) as { message?: string };
        setError(body.message ?? `request failed (${res.status})`);
        return;
      }
      setSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "request failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Stack.Screen options={{ title: "Reset password" }} />
      <KeyboardAvoidingView
        behavior={Platform.OS === "ios" ? "padding" : undefined}
        style={{ flex: 1 }}
      >
      <View style={styles.container}>
        <Text style={styles.title}>Reset your password</Text>
        {sent ? (
          <View style={styles.form}>
            {/* Brief: honest microcopy. Don't reveal whether the email
                exists in our DB — BetterAuth returns the same response
                either way for enumeration protection. */}
            <Text style={styles.notice} accessibilityLiveRegion="polite">
              If that email is on file, we just sent a reset link. Check your
              inbox.
            </Text>
            <Button
              label="Back to login"
              onPress={() => router.replace("/login")}
              variant="secondary"
              fullWidth
            />
          </View>
        ) : (
          <View style={styles.form}>
            <Text style={styles.subtitle}>
              Enter your email and we'll send you a link to set a new password.
            </Text>
            <TextInput
              style={styles.input}
              placeholder="Email"
              placeholderTextColor={colors.textMuted}
              value={email}
              onChangeText={setEmail}
              autoCapitalize="none"
              keyboardType="email-address"
              autoComplete="email"
              accessibilityLabel="Email address"
            />
            {error ? (
              <Text style={styles.error} accessibilityLiveRegion="polite">
                {error}
              </Text>
            ) : null}
            <Button
              label="Send reset link"
              onPress={handleSubmit}
              disabled={!email}
              busy={busy}
              fullWidth
              accessibilityLabel="Send reset link"
            />
            <Link href="/login" asChild>
              <Button
                label="Back to login"
                variant="ghost"
                fullWidth
                accessibilityLabel="Back to login"
              />
            </Link>
          </View>
        )}
      </View>
      </KeyboardAvoidingView>
    </>
  );
}

function makeStyles(colors: Colors) {
  return StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
    alignItems: "center",
    justifyContent: "center",
    padding: space[6],
  },
  title: { ...textStyle.h2, color: colors.heading },
  subtitle: {
    ...textStyle.bodySmall,
    color: colors.textSecondary,
  },
  form: { marginTop: space[6], gap: space[3], width: layout.formMaxWidth },
  input: {
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: radius.md,
    paddingVertical: space[3],
    paddingHorizontal: space[4],
    fontSize: 16,
    fontFamily: fontFamily.regular,
    color: colors.textPrimary,
    backgroundColor: colors.surface,
  },
  error: { ...textStyle.bodySmall, color: colors.error },
  notice: { ...textStyle.body, color: colors.success },
  });
}
